import { zGet, zForm } from "./zulipClient";
import type { ZulipChannelFolder, ZulipEvent, ZulipMessage, ZulipStream, ZulipTopic, ZulipUser } from "./types";

// Domain-specific Zulip calls, built on top of zulipClient's transport
// (zGet/zForm). See the plan doc for why each endpoint was chosen — in
// short: everything here is the *normal member* API surface, distinct from
// the admin bot-account endpoints ZulipService.php already exposes.

/** GET users/me/subscriptions — channels the current user has joined. */
export async function getMySubscriptions(): Promise<ZulipStream[]> {
  const res = await zGet("users/me/subscriptions");
  return res.subscriptions ?? [];
}

/** GET streams — every stream visible to the caller (public + already-subscribed private), for the "browse/join" list. */
export async function getAllStreams(): Promise<ZulipStream[]> {
  const res = await zGet("streams", { include_public: true, include_subscribed: true });
  return res.streams ?? [];
}

/** GET users/me/{stream_id}/topics — topics within a channel, most-recent-first. */
export async function getStreamTopics(streamId: number): Promise<ZulipTopic[]> {
  const res = await zGet(`users/me/${streamId}/topics`);
  return res.topics ?? [];
}

type MessagesResult = {
  messages: ZulipMessage[];
  found_newest?: boolean;
  found_oldest?: boolean;
};

/**
 * GET messages — history for a narrow. `anchor` defaults to "newest" for the
 * initial load; pass an older message id + numBefore to page backwards.
 */
export async function getMessages(opts: {
  narrow: Array<{ operator: string; operand: unknown }>;
  anchor?: number | "newest" | "first_unread";
  numBefore?: number;
  numAfter?: number;
  /** Pass false when polling for messages strictly after `anchor` (an already-seen id) to avoid re-fetching it. */
  includeAnchor?: boolean;
}): Promise<MessagesResult> {
  return zGet("messages", {
    narrow: opts.narrow,
    anchor: opts.anchor ?? "newest",
    num_before: opts.numBefore ?? 50,
    num_after: opts.numAfter ?? 0,
    include_anchor: opts.includeAnchor ?? true,
  });
}

/** POST messages — sends the first message to a new topic name to "create" it. */
export async function sendStreamMessage(streamName: string, topic: string, content: string): Promise<void> {
  await zForm("POST", "messages", { type: "stream", to: streamName, topic, content });
}

/** POST messages — direct message to one or more recipients (group DM if more than one). */
export async function sendDirectMessage(userIds: number[], content: string): Promise<void> {
  await zForm("POST", "messages", { type: "private", to: userIds, content });
}

/**
 * POST users/me/subscriptions — creates a channel as a normal member (not
 * the admin bot flow), or invites additional members to an existing one when
 * `name` already exists. One endpoint covers create / join / invite.
 */
export async function createOrJoinChannel(opts: {
  name: string;
  description?: string;
  inviteOnly?: boolean;
  principals?: number[];
}): Promise<any> {
  return zForm("POST", "users/me/subscriptions", {
    subscriptions: [{ name: opts.name, description: opts.description ?? "" }],
    invite_only: !!opts.inviteOnly,
    history_public_to_subscribers: true,
    principals: opts.principals ?? [],
  });
}

/** Invite additional members to an existing channel — same endpoint as create. */
export async function inviteToChannel(streamName: string, principals: number[]): Promise<any> {
  return zForm("POST", "users/me/subscriptions", {
    subscriptions: [{ name: streamName }],
    principals,
  });
}

/**
 * POST users/me/subscriptions/properties — pins/unpins a channel in the
 * caller's own sidebar (pin_to_top on their subscription). Purely personal:
 * it only affects how channelGrouping.ts buckets this user's own
 * `subscriptions`, never anyone else's. Requires the caller to already be
 * subscribed — every channel this can be called for necessarily is, since
 * it's only ever surfaced next to rows drawn from `subscriptions`.
 */
export async function setChannelPinned(streamId: number, pinned: boolean): Promise<void> {
  await zForm("POST", "users/me/subscriptions/properties", {
    subscription_data: [{ stream_id: streamId, property: "pin_to_top", value: pinned }],
  });
}

/** GET users — realm members, for the invite/new-DM people-picker. */
export async function getRealmUsers(): Promise<ZulipUser[]> {
  const res = await zGet("users", { client_gravatar: false });
  return (res.members ?? []).filter((u: ZulipUser) => !u.is_bot);
}

/** GET channel_folders — the realm's channel folders, used to group the channel list the way Zulip's own left sidebar does. */
export async function getChannelFolders(): Promise<ZulipChannelFolder[]> {
  const res = await zGet("channel_folders");
  return res.channel_folders ?? [];
}

type RealmPresenceResult = {
  presences: Record<string, { aggregated?: { status: "active" | "idle"; timestamp: number } }>;
  server_timestamp: number;
};

/** GET realm/presence — every realm member's latest presence (keyed by email), for the people list's online/idle/offline dots. */
export async function getRealmPresence(): Promise<RealmPresenceResult> {
  return zGet("realm/presence");
}

/**
 * POST users/me/presence — reports the caller's own presence. Zulip's
 * presence system is opt-in per client: a user's row in realm/presence is
 * only ever as fresh as the last ping some client sent on their behalf, so
 * without this call every user reads as offline in the people list
 * regardless of how active they actually are. ping_only skips the (unused
 * here) presence payload the endpoint would otherwise echo back.
 */
export async function updateOwnPresence(status: "active" | "idle"): Promise<void> {
  await zForm("POST", "users/me/presence", { status, ping_only: true, new_user_input: status === "active" });
}

export type RegisterResult = {
  queue_id: string;
  last_event_id: number;
  unread_msgs: {
    count: number;
    streams: Array<{ stream_id: number; topic: string; unread_message_ids: number[] }>;
    pms: Array<{ sender_id: number; unread_message_ids: number[] }>;
    huddles: Array<{ user_ids_string: string; unread_message_ids: number[] }>;
    // Message ids that are both unread and mention the caller (direct or
    // wildcard) — a subset of the ids already counted in streams/pms/huddles
    // above, not additional unread messages.
    mentions?: number[];
  };
  // How often a client should re-send "start" while typing continues, and
  // how long of a pause before it must send "stop" — server-configured, so
  // read from here rather than hardcoded (older servers may omit them).
  server_typing_started_wait_period_milliseconds?: number;
  server_typing_stopped_wait_period_milliseconds?: number;
  // Static-asset URL for Zulip's own canonical unicode-emoji name data
  // (code_to_names, keyed by hex codepoint) — see emojiCodeMap.ts for why
  // reactions need it. Only present because "realm" is in event_types below.
  server_emoji_data_url?: string;
  // The realm's read-receipts master switch ("Enable read receipts" in
  // Organization settings) — only present because "realm" is in
  // event_types below. Distinct from a user's own per-account
  // "send_read_receipts" privacy setting, which getReadReceipts()'s
  // response already accounts for server-side.
  realm_enable_read_receipts?: boolean;
};

/**
 * POST register — opens a real-time event queue and seeds the initial
 * unread snapshot (see useZulipEvents.ts, which also re-calls this to
 * recover after a queue expires). `apply_markdown` must be passed explicitly
 * here: unlike GET /messages (defaults true), register's default is
 * *false* — without this, `message` events delivered via the queue would
 * carry raw Markdown instead of the rendered HTML MessageBubble expects.
 * "realm" is included in event_types solely to get `realm_enable_read_receipts`
 * in the response below; any other "realm" events it causes the queue to
 * deliver are harmlessly ignored (see useZulipEvents' handleEvent).
 * "update_message" is required for inline image previews: a freshly uploaded
 * image is embedded with a placeholder spinner src while Zulip's async
 * thumbnail worker generates the real thumbnail, then patched in via an
 * `update_message` event (`do_update_embedded_data` in
 * zerver/actions/message_edit.py) — without subscribing to it, the
 * placeholder never gets replaced.
 * "reaction" delivers add/remove events for message reactions (including the
 * caller's own — see notify_reaction_update in zerver/actions/reactions.py),
 * so every open client's reaction pills stay in sync without polling.
 * `client_capabilities.stream_typing_notifications` must be explicitly
 * opted into (defaults false server-side, for backwards compat with older
 * clients) or the queue silently drops every channel/topic "typing" event
 * before it's ever delivered — direct-message typing has no such gate, which
 * is why without this a DM's typing indicator works but a channel's never
 * fires (see accepts_event in Zulip's zerver/tornado/event_queue.py).
 * notification_settings_null is unrelated to typing but is technically a
 * required field on this dict — set true to match modern Zulip clients.
 */
export async function registerQueue(): Promise<RegisterResult> {
  return zForm("POST", "register", {
    event_types: ["message", "update_message", "reaction", "subscription", "update_message_flags", "typing", "realm"],
    apply_markdown: true,
    client_capabilities: { notification_settings_null: true, stream_typing_notifications: true },
  });
}

/**
 * POST typing — tells Zulip the user is composing (or has stopped composing)
 * a message, so other clients viewing the same conversation can show a
 * "so-and-so is typing…" indicator (delivered back to them as a "typing"
 * event via their own event queue).
 */
export async function sendTypingStatus(
  op: "start" | "stop",
  target: { kind: "dm"; userIds: number[] } | { kind: "channel"; streamId: number; topic: string },
): Promise<void> {
  if (target.kind === "dm") {
    await zForm("POST", "typing", { op, type: "direct", to: target.userIds });
  } else {
    await zForm("POST", "typing", { op, type: "stream", stream_id: target.streamId, topic: target.topic });
  }
}

/**
 * GET events — long-polls the queue from `registerQueue()`. Blocks
 * server-side until a new event arrives or a few minutes pass (the server
 * sends a "heartbeat" event to keep the connection alive), then returns.
 * Call again immediately with the highest event id seen so far.
 */
export async function getEvents(queueId: string, lastEventId: number): Promise<{ events: ZulipEvent[] }> {
  return zGet("events", { queue_id: queueId, last_event_id: lastEventId, dont_block: false });
}

/**
 * POST/DELETE messages/{id}/reactions — adds or removes one of the caller's
 * own reactions on a message. `emoji_code` is always computed client-side
 * from the emoji's own Unicode codepoints (see MessageBubble's
 * unicodeEmojiCode) rather than left for Zulip to resolve. `emoji_name`
 * still matters, though: Zulip's check_emoji_request (zerver/lib/emoji.py)
 * validates that emoji_name maps back to the exact emoji_code sent, via its
 * own internal name table — it does NOT accept an arbitrary/mismatched name
 * once a valid code is present. emoji-picker-react's own names essentially
 * never match that table (e.g. "thumbs up" vs Zulip's "+1"), so callers
 * should resolve emoji_name via emojiCodeMap.ts's Zulip-sourced name data
 * rather than the picker's own `names` field — see ChatContext's
 * toggleReaction, which does this before calling either function below.
 */
export async function addReaction(messageId: number, emojiName: string, emojiCode: string, reactionType: string): Promise<void> {
  await zForm("POST", `messages/${messageId}/reactions`, { emoji_name: emojiName, emoji_code: emojiCode, reaction_type: reactionType });
}

/** See addReaction — same params, removes the caller's matching reaction instead. */
export async function removeReaction(messageId: number, emojiName: string, emojiCode: string, reactionType: string): Promise<void> {
  await zForm("DELETE", `messages/${messageId}/reactions`, { emoji_name: emojiName, emoji_code: emojiCode, reaction_type: reactionType });
}

/** POST messages/flags — marks messages read once their thread is viewed. */
export async function markMessagesRead(messageIds: number[]): Promise<void> {
  if (!messageIds.length) return;
  await zForm("POST", "messages/flags", { messages: messageIds, op: "add", flag: "read" });
}

/**
 * GET messages/{id}?apply_markdown=false — the message's raw Zulip-flavored
 * Markdown source, rather than the rendered HTML `ZulipMessage.content`
 * normally carries (getMessages/registerQueue both pass apply_markdown:
 * true). Quoting/forwarding needs to re-embed the original source text, not
 * re-quote already-rendered HTML — mirrors Zulip's own
 * message_fetch_raw_content.ts (get_raw_content_for_single_message).
 */
export async function getRawMessageContent(messageId: number): Promise<string> {
  const res = await zGet(`messages/${messageId}`, { apply_markdown: false });
  return res.message?.content ?? "";
}

/**
 * GET messages/{id}/read_receipts — user ids (excluding the sender) who have
 * read this message. Per-message and on-demand only: Zulip has no bulk or
 * list-level read-receipts endpoint, so this must be called per message id a
 * caller actually wants to know about. Rejects (e.g. "Read receipts are
 * disabled in this organization") when the realm's `enable_read_receipts`
 * master switch is off — see `RegisterResult.realm_enable_read_receipts`,
 * which callers should check before calling this at all.
 */
export async function getReadReceipts(messageId: number): Promise<number[]> {
  const res = await zGet(`messages/${messageId}/read_receipts`);
  return res.user_ids ?? [];
}
